/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-prototype-builtins */
import { registerLocaleData } from '@angular/common';
import it from '@angular/common/locales/it';
import { ApplicationConfig } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { BackendConfig, MicroserviceId } from '@eng-dx-gpt/api-client';
import { LoadingConfig } from '@eng-dx-gpt/loading';
import { LoggerConfig } from '@eng-dx-gpt/logger';
import { unflatten } from 'flat';
import { ChatbotConfig, chatbotConfig } from '../config/chatbot.config';
import {
    authenticationConf,
    AuthenticationConf,
    backendConfig,
    dateFnsConfig,
    DateFnsConfig,
    frontendConfig,
    FrontendConfig,
    i18nConfig,
    I18nConfig,
    knowledgeBaseConfig,
    KnowledgeBaseConfig,
    loadingConfig,
    loggerConfig,
    previewConfig,
    PreviewConfigInterface,
    uiConfig,
    UIConfig
} from './../config';
import { routes } from './app.routes';
import { provideCore } from './core/provide-core';
import { provideTheme } from './theme/provide-theme';

registerLocaleData(it);

export const getAngularAppConfig: (config: InternalAppConfig) => ApplicationConfig = (config: InternalAppConfig) => ({
    providers: [provideRouter(routes), provideAnimations(), provideCore(config), provideTheme(config.ui)]
});

interface FlatAppConfig {
    'authentication.loginUrl': string;
    'authentication.cognito.enabled': boolean;
    'authentication.cognito.userPoolId': string;
    'authentication.cognito.clientId': string;
    'authentication.cognito.domain': string;
    'authentication.cognito.region': string;
    'authentication.cognito.redirectUri': string;
    'authentication.cognito.logoutUri': string;
    'backend.baseUrl': Record<MicroserviceId, string> | string;
    'backend.requestHeaders': Record<string, string> | Record<MicroserviceId, Record<string, string>>;
    'frontend.appBasePath': string;
    'logger.level': number;
    'preview.mainJs': string;
    'preview.styleCss': string;
    'dateFns.format': string;
    'loading.enabled': boolean;
    'knowledgeBase.maxLength': number;
    'ui.logo': string;
    'ui.vars': Record<string, string>;
    'ui.font-family': string;
    'i18n.defaultLang': string;
    'i18n.availableLanguages': string[];
    'i18n.translationsPath': string;
    'chatbot.key': string;
    'chatbot.main': string;
    'chatbot.startup': string;
    'chatbot.styles': string;
}

const merge = <T>(dest: T, source: T): T => {
    const target = {} as T;
    // Merge the object into the target object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const merger = (obj: any) => {
        for (const prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                if (Object.prototype.toString.call(obj[prop]) === '[object Object]') {
                    // If we're doing a deep merge
                    // and the property is an object
                    // @ts-ignore
                    target[prop] = merge(target[prop], obj[prop]);
                } else {
                    // Otherwise, do a regular merge
                    // @ts-ignore
                    target[prop] = obj[prop];
                }
            }
        }
    };
    merger(dest);
    merger(source);
    return target;
};

class InternalAppConfig {
    authentication: AuthenticationConf = authenticationConf;
    backend: Partial<BackendConfig> = backendConfig;
    chatbot: ChatbotConfig = chatbotConfig;
    dateFns: DateFnsConfig = dateFnsConfig;
    frontend: FrontendConfig = frontendConfig;
    i18n: I18nConfig = i18nConfig;
    knowledgeBase: KnowledgeBaseConfig = knowledgeBaseConfig;
    loading: Partial<LoadingConfig> = loadingConfig;
    logger: LoggerConfig = loggerConfig;
    preview: PreviewConfigInterface = previewConfig;
    ui: Partial<UIConfig> = uiConfig;

    extend(flatAppConfig: FlatAppConfig): InternalAppConfig {
        const target = merge(this, unflatten<FlatAppConfig, InternalAppConfig>(flatAppConfig));

        return Object.assign(this, target);
    }
}

export const RUNTIME_APP_CONFIG = new InternalAppConfig();
